import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { serviceChooseBusinessStart } from 'store/authentication/authenticationAction';
import { Button } from '@kizen/kds/Button';
import { Typography } from '@kizen/kds/Typography';
import Input from '__components/Kizen/Input';
import Loader from '__components/Kizen/Loader';
import {
  Screen,
  StyledCard,
  Wrapper,
  Header,
  FormWrapper,
  InputFields,
  Footer,
} from './styles';
import BusinessService from 'services/BusinessService';
import {
  DATA_MANAGER_SESSION_ACTIONS,
  useDataManagerSession,
} from 'app/dataManagerSession';
import { oauthSessionStorage } from 'utility/_oauthSessionStorage';
import { postChangeBusinessMessage } from 'store/authentication/channels';
import { selectedBusinessLocalStorage } from 'storage';
import { getChosenBusiness } from 'store/authentication/selectors';

const CreateNewBusiness = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { dispatchSessionState } = useDataManagerSession() as any;
  const instanceId = useSelector((s: any) => s.authentication.instanceId);
  const { company_name } = useSelector(getChosenBusiness) ?? {};

  const [companyName, setCompanyName] = useState(company_name || '');
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isValid = useMemo(() => {
    return companyName.length > 0 && businessName.length > 0;
  }, [companyName, businessName]);

  const handleCreateNewBusiness = useCallback(
    async (createWithAI: boolean) => {
      try {
        setLoading(true);
        const newBusiness = await BusinessService.createNewBusiness({
          company_name: companyName,
          business_name: businessName,
        });

        const [error] = await (dispatch as any)(
          serviceChooseBusinessStart(newBusiness?.id as string)
        );

        (dispatchSessionState as any)({
          type: DATA_MANAGER_SESSION_ACTIONS.DELETE,
        });

        if (!error) {
          oauthSessionStorage.clear();
          if (createWithAI) {
            setLoading(false);
            history.push('/businessbuilder');
          } else {
            history.push('/dashboard');
          }
          postChangeBusinessMessage(instanceId);
          selectedBusinessLocalStorage.val = newBusiness?.id;
        }
        setLoading(false);
      } catch (error: any) {
        setLoading(false);
        setError(error?.response?.data?.detail);
      }
    },
    [
      companyName,
      businessName,
      history,
      dispatch,
      dispatchSessionState,
      instanceId,
    ]
  );

  return (
    <Screen>
      <StyledCard>
        <Wrapper>
          <Header>
            <Typography size="xl">{t('Create New Kizen Business')}</Typography>
          </Header>
          {!loading ? (
            <FormWrapper>
              <InputFields>
                <Input
                  type={Input.INPUT_TYPES.TEXT}
                  label={t('Company Name*')}
                  value={companyName}
                  onChange={setCompanyName}
                  placeholder={t('Enter Company Name')}
                  tabIndex={1}
                  fullWidth
                />
                <Input
                  label={t('Kizen Business Name*')}
                  value={businessName}
                  onChange={setBusinessName}
                  placeholder={t('Enter Kizen Business Name')}
                  tabIndex={2}
                  fullWidth
                />
              </InputFields>
              {error !== '' ? (
                <Typography size="sm" color="red-700" enableTooltip={false}>
                  {error}
                </Typography>
              ) : null}
              <Footer>
                <Button
                  outline
                  onClick={() => handleCreateNewBusiness(false)}
                  disabled={!isValid || loading}
                >
                  {t('Create from Scratch')}
                </Button>
                <Button
                  onClick={() => handleCreateNewBusiness(true)}
                  disabled={!isValid || loading}
                >
                  {t('Create with AI')}
                </Button>
              </Footer>
            </FormWrapper>
          ) : (
            <Loader loading />
          )}
        </Wrapper>
      </StyledCard>
    </Screen>
  );
};

export default CreateNewBusiness;
